import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SubusersService } from '../../api/generated/services/SubusersService';
import type { SubuserView } from '../../api/generated/models/SubuserView';
import type { CreateSubuserRequest } from '../../api/generated/models/CreateSubuserRequest';
import type { DeleteSubuserRequest } from '../../api/generated/models/DeleteSubuserRequest';
import type { UpdateSubuserRequest } from '../../api/generated/models/UpdateSubuserRequest';
import { startLoading, stopLoading } from './loadingSlice';
import { ApiError } from '../../api/generated/core/ApiError';

interface SubusersState {
  subusers: SubuserView[];
  maxAllowed: number;
  error: string | null;
  createLoading: boolean;
  deleteLoading: boolean;
  updateLoading: boolean;
  isLoaded: boolean;
}

const initialState: SubusersState = {
  subusers: [],
  maxAllowed: 0,
  error: null,
  createLoading: false,
  deleteLoading: false,
  updateLoading: false,
  isLoaded: false,
};

export const fetchSubusers = createAsyncThunk(
  'subusers/fetchSubusers',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await SubusersService.getAppSubuserList();
      return response;
    } catch (error: any) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch subusers');
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchMaxAllowedSubusers = createAsyncThunk(
  'subusers/fetchMaxAllowed',
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubusersService.getAppSubuserMaxAllowed();
      return response;
    } catch (error: any) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to fetch max allowed subusers');
    }
  }
);

export const createSubuser = createAsyncThunk(
  'subusers/createSubuser',
  async (subuserData: CreateSubuserRequest, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await SubusersService.postAppSubuserCreate(subuserData);
      return response;
    } catch (error: any) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to create subuser');
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateSubuserPermissions = createAsyncThunk(
  'subusers/updatePermissions',
  async (data: UpdateSubuserRequest, { rejectWithValue }) => {
    try {
      const response = await SubusersService.putAppSubuserUpdate(data);
      return response;
    } catch (error: any) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to update permissions');
    }
  }
);

export const deleteSubuser = createAsyncThunk(
  'subusers/deleteSubuser',
  async (subuserData: DeleteSubuserRequest, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    try {
      await SubusersService.deleteAppSubuserDelete(subuserData);
      return subuserData.id;
    } catch (error: any) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to delete subuser');
    } finally {
      dispatch(stopLoading());
    }
  }
);

const subusersSlice = createSlice({
  name: 'subusers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch subusers
      .addCase(fetchSubusers.pending, (state) => {
        state.error = null;
        state.isLoaded = false;
      })
      .addCase(fetchSubusers.fulfilled, (state, action) => {
        state.subusers = action.payload || [];
        state.isLoaded = true;
      })
      .addCase(fetchSubusers.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to fetch subusers';
        state.isLoaded = true;
      })
      // Fetch max allowed
      .addCase(fetchMaxAllowedSubusers.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchMaxAllowedSubusers.fulfilled, (state, action) => {
        state.maxAllowed = (action.payload as any)?.maxAllowed || 0;
      })
      .addCase(fetchMaxAllowedSubusers.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to fetch max allowed subusers';
      })
      // Create subuser
      .addCase(createSubuser.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createSubuser.fulfilled, (state, action) => {
        state.createLoading = false;
        state.subusers.push(action.payload);
      })
      .addCase(createSubuser.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string || 'Failed to create user';
      })
      // Update permissions
      .addCase(updateSubuserPermissions.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateSubuserPermissions.fulfilled, (state, action) => {
        state.updateLoading = false;
        const idx = state.subusers.findIndex(s => s.id === action.payload.id);
        if (idx !== -1) state.subusers[idx] = action.payload;
      })
      .addCase(updateSubuserPermissions.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload as string || 'Failed to update permissions';
      })
      // Delete subuser
      .addCase(deleteSubuser.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteSubuser.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.subusers = state.subusers.filter(subuser => subuser.id !== action.payload);
      })
      .addCase(deleteSubuser.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string || 'Failed to delete user';
      });
  },
});

export const { clearError } = subusersSlice.actions;
export type { UpdateSubuserRequest };
export default subusersSlice.reducer;
